import * as React from 'react';
import ReactPixel from 'react-facebook-pixel';
import TextPage from '../components/TextPage';

export function PrivacyPolicy() {
  return (
    <div>
      {ReactPixel.pageView()}
      <TextPage>
        <h1>PannaDraws Privacy Policy</h1>
        <p>Last updated: April 2026</p>

        <h2>1. Who I Am</h2>
        <p>
          PannaDraws is operated by Panna Zsamba, based in Ireland. For the
          purposes of data protection law, I am the data controller in respect
          of your personal data.
        </p>
        <ul>
          <li>
            Email:{' '}
            <a href="mailto:panna@pannadraws.com">panna@pannadraws.com</a>
          </li>
          <li>
            Website:{' '}
            <a
              href="https://pannadraws.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://pannadraws.com
            </a>
          </li>
        </ul>
        <p>
          Ireland's data protection supervisory authority is the Data Protection
          Commission (DPC):{' '}
          <a
            href="https://www.dataprotection.ie"
            target="_blank"
            rel="noopener noreferrer"
          >
            dataprotection.ie
          </a>
        </p>

        <h2>2. What Data I Collect and Why</h2>
        <p>I collect and use personal data in the following ways:</p>

        <h3>a) When you contact me or place a commission enquiry</h3>
        <ul>
          <li>
            <strong>Data collected:</strong> your name, email address, and any
            information you include in your message.
          </li>
          <li>
            <strong>Purpose:</strong> to respond to your enquiry and manage your
            commission request.
          </li>
          <li>
            <strong>Lawful basis:</strong> performance of a contract (or steps
            taken at your request prior to entering a contract).
          </li>
        </ul>

        <h3>b) When you make a purchase through Etsy</h3>
        <p>
          Purchases are processed through Etsy. Etsy handles payment and order
          data directly. I receive your name, shipping address, and order
          details from Etsy in order to fulfil your order.
        </p>
        <p>
          For international orders, I may contact you through Etsy messaging to
          request your phone number and email address. This is necessary so that
          the courier can reach you to arrange delivery, and so that customs
          authorities can notify you of any customs charges payable before or
          upon delivery.
        </p>
        <ul>
          <li>
            <strong>Purpose:</strong> to fulfil your order and comply with my
            legal obligations (e.g. tax records).
          </li>
          <li>
            Your phone number and email address for international orders are
            shared only with the courier and, where required, with the relevant
            customs authority.
          </li>
          <li>
            <strong>Lawful basis:</strong> performance of a contract and
            compliance with a legal obligation.
          </li>
        </ul>

        <h3>c) When you visit my website</h3>
        <p>
          I collect usage data automatically, including your IP address, browser
          type and version, the pages you visit, and the time and duration of
          your visit.
        </p>
        <ul>
          <li>
            <strong>Purpose:</strong> to monitor website performance and improve
            my service.
          </li>
          <li>
            <strong>Lawful basis:</strong> legitimate interests (understanding
            how my site is used, in a way that does not override your rights).
          </li>
        </ul>

        <h3>d) Marketing communications</h3>
        <p>
          If you have opted in to receive news or updates from me, I may contact
          you by email about new artwork, offers, or events.
        </p>
        <ul>
          <li>
            <strong>Purpose:</strong> direct marketing.
          </li>
          <li>
            <strong>Lawful basis:</strong> consent. You can withdraw your
            consent at any time by contacting me at{' '}
            <a href="mailto:panna@pannadraws.com">panna@pannadraws.com</a> or
            unsubscribing from any marketing email.
          </li>
        </ul>

        <h2>3. Cookies and Tracking Technologies</h2>
        <p>
          My website uses cookies and similar tracking technologies. Cookies are
          small files placed on your device that help me understand how the site
          is used.
        </p>
        <p>I use the following types of cookies:</p>
        <ul>
          <li>
            <strong>Essential cookies</strong> — necessary for the website to
            function. These cannot be disabled.
          </li>
          <li>
            <strong>Analytics cookies</strong> — used to understand how visitors
            use my site (e.g. Google Analytics). These are only set with your
            consent.
          </li>
          <li>
            <strong>Advertising/tracking cookies</strong> — used to measure the
            effectiveness of advertising (e.g. Facebook Pixel). These are only
            set with your consent.
          </li>
        </ul>
        <p>
          You will be asked for your consent before any non-essential cookies
          are placed on your device. You can change your cookie preferences at
          any time via the cookie settings on my site, or by adjusting your
          browser settings.
        </p>
        <p>
          <strong>Google Analytics:</strong> Data is processed by Google LLC.
          For more information, see{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google's privacy policy
          </a>
          .
        </p>
        <p>
          <strong>Facebook Pixel:</strong> Data is processed by Meta Platforms,
          Inc. This tool may transfer data to the United States. Such transfers
          are covered by Meta's participation in the EU–US Data Privacy
          Framework. For more information, see{' '}
          <a
            href="https://www.facebook.com/privacy/policy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Meta's privacy policy
          </a>
          .
        </p>

        <h2>4. Who I Share Your Data With</h2>
        <p>
          I do not sell your personal data. I may share it in the following
          limited circumstances:
        </p>
        <ul>
          <li>
            <strong>Service providers</strong> — third parties who help me
            operate my website or business (e.g. analytics providers), who are
            bound by data processing agreements and may only use your data on my
            behalf.
          </li>
          <li>
            <strong>Etsy</strong> — if you purchase through my Etsy shop, Etsy's
            own privacy policy applies to the data they process. See{' '}
            <a
              href="https://www.etsy.com/ie/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Etsy's privacy policy
            </a>
            .
          </li>
          <li>
            <strong>Legal obligations</strong> — if required to do so by law or
            by a public authority (e.g. a court or government agency).
          </li>
          <li>
            <strong>Business transfers</strong> — in the event of a merger,
            sale, or acquisition, your data may be transferred. I will notify
            you before this happens.
          </li>
        </ul>

        <h2>5. International Data Transfers</h2>
        <p>
          Some of my service providers (including Google and Meta) are based
          outside the European Economic Area (EEA). Where personal data is
          transferred outside the EEA, I ensure appropriate safeguards are in
          place, such as the EU–US Data Privacy Framework or standard
          contractual clauses approved by the European Commission.
        </p>

        <h2>6. How Long I Keep Your Data</h2>
        <p>I keep your personal data only for as long as necessary:</p>
        <ul>
          <li>
            <strong>Enquiry and commission correspondence:</strong> up to 2
            years after the last contact, unless a longer period is required for
            legal reasons.
          </li>
          <li>
            <strong>Order records:</strong> up to 7 years, to comply with Irish
            tax and accounting obligations.
          </li>
          <li>
            <strong>Website analytics data:</strong> typically up to 26 months,
            in line with standard analytics retention periods.
          </li>
          <li>
            <strong>Marketing:</strong> until you withdraw your consent.
          </li>
        </ul>

        <h2>7. Your Rights Under GDPR</h2>
        <p>As a data subject under GDPR, you have the following rights:</p>
        <ul>
          <li>
            <strong>Right of access</strong> — you can request a copy of the
            personal data I hold about you.
          </li>
          <li>
            <strong>Right to rectification</strong> — you can ask me to correct
            inaccurate data.
          </li>
          <li>
            <strong>Right to erasure</strong> — you can ask me to delete your
            data in certain circumstances.
          </li>
          <li>
            <strong>Right to restrict processing</strong> — you can ask me to
            limit how I use your data.
          </li>
          <li>
            <strong>Right to data portability</strong> — you can request your
            data in a machine-readable format.
          </li>
          <li>
            <strong>Right to object</strong> — you can object to processing
            based on legitimate interests or for direct marketing purposes.
          </li>
          <li>
            <strong>Right to withdraw consent</strong> — where I rely on
            consent, you can withdraw it at any time without affecting the
            lawfulness of prior processing.
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact me at{' '}
          <a href="mailto:panna@pannadraws.com">panna@pannadraws.com</a>. I will
          respond within one month.
        </p>
        <p>
          You also have the right to lodge a complaint with the Data Protection
          Commission (DPC) at any time:{' '}
          <a
            href="https://www.dataprotection.ie"
            target="_blank"
            rel="noopener noreferrer"
          >
            dataprotection.ie
          </a>{' '}
          / 01 765 0100.
        </p>

        <h2>8. Children's Privacy</h2>
        <p>
          My website is not directed at children. In Ireland, the age of digital
          consent is 16. I do not knowingly collect personal data from anyone
          under 16. If you believe a child under 16 has provided me with
          personal data, please contact me at{' '}
          <a href="mailto:panna@pannadraws.com">panna@pannadraws.com</a> and I
          will delete it promptly.
        </p>

        <h2>9. Links to Other Websites</h2>
        <p>
          My website may contain links to third-party sites. I am not
          responsible for the privacy practices of those sites and recommend you
          review their privacy policies before sharing any personal data with
          them.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          I may update this policy from time to time. I will notify you of
          significant changes by posting a notice on my website and updating the
          "Last updated" date above. I encourage you to review this policy
          periodically.
        </p>

        <h2>11. Contact</h2>
        <p>
          For any questions about this privacy policy or how I handle your data:
        </p>
        <ul>
          <li>
            Email:{' '}
            <a href="mailto:panna@pannadraws.com">panna@pannadraws.com</a>
          </li>
          <li>
            Website:{' '}
            <a
              href="https://pannadraws.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://pannadraws.com
            </a>
          </li>
        </ul>
      </TextPage>
    </div>
  );
}
